"use client";

import type { FormEvent } from "react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MANUAL_SOURCE_TYPE_OPTIONS } from "@/lib/source-types";

interface EditForm {
  title?: string;
  author?: string;
  issued?: string;
  URL?: string;
  type?: string;
}

const DEFAULT_SOURCE_TYPE = MANUAL_SOURCE_TYPE_OPTIONS[0].cslType;

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: EditForm;
  onFormChange: (form: EditForm) => void;
  onSubmit: (e: FormEvent) => void;
  saving: boolean;
}

export function EditDialog({
  open,
  onOpenChange,
  form,
  onFormChange,
  onSubmit,
  saving,
}: EditDialogProps) {
  const s = useToolStrings();
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{s.quickEdit}</DialogTitle>
          <DialogDescription>{s.quickEditHint}</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" id="edit-form" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="edit-type">{s.sourceType}</Label>
            <Select
              onValueChange={(value) => onFormChange({ ...form, type: value })}
              value={form.type ?? DEFAULT_SOURCE_TYPE}
            >
              <SelectTrigger className="w-full" id="edit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_SOURCE_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.cslType} value={option.cslType}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-title">{s.fieldTitleShort}</Label>
            <Input
              id="edit-title"
              onChange={(e) => onFormChange({ ...form, title: e.target.value })}
              value={form.title ?? ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-author">{s.fieldAuthor}</Label>
            <Input
              id="edit-author"
              onChange={(e) =>
                onFormChange({ ...form, author: e.target.value })
              }
              value={form.author ?? ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-date">{s.fieldDateShort}</Label>
            <Input
              id="edit-date"
              onChange={(e) =>
                onFormChange({ ...form, issued: e.target.value })
              }
              value={form.issued ?? ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-url">URL</Label>
            <Input
              id="edit-url"
              onChange={(e) => onFormChange({ ...form, URL: e.target.value })}
              type="url"
              value={form.URL ?? ""}
            />
          </div>
        </form>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Cancel
          </Button>
          <Button disabled={saving} form="edit-form" type="submit">
            {saving ? s.saving : s.update}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
